import { Shield, Baby, Crown } from 'lucide-react';
import { Badge, BadgeVariant } from '@/shared/components/ui';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { TruncatedText } from '@/shared/components/ui/truncated-text';
import { cn } from '@/shared/lib/utils';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { UserRole } from '../types/auth.types';

interface RoleIndicatorProps {
  /** Show only the badge without tooltip details */
  readonly compact?: boolean;
  /** Additional CSS classes */
  readonly className?: string;
}

/**
 * Displays the current user's role with an icon and badge.
 * Hover to see active permissions (when not in compact mode).
 *
 * @example
 * // In header - compact mode
 * <RoleIndicator compact />
 *
 * @example
 * // Full mode with permission tooltip
 * <RoleIndicator />
 */
export function RoleIndicator({
  compact = false,
  className,
}: RoleIndicatorProps) {
  const { user, role, isAuthenticated } = useAuth();
  const { permissions } = usePermissions();

  if (!isAuthenticated || !role || !permissions) {
    return null;
  }

  const isSuperAdmin = role === UserRole.SUPER_ADMIN;
  const isParent = isSuperAdmin || role === UserRole.PARENT;
  const Icon = isSuperAdmin ? Crown : isParent ? Shield : Baby;
  const roleLabel = isSuperAdmin ? 'Основател' : isParent ? 'Родител' : 'Дете';
  const roleEnglish = isSuperAdmin ? 'Founder' : isParent ? 'Parent' : 'Child';
  const variant = isParent ? BadgeVariant.Default : BadgeVariant.Secondary;

  // Get list of active permissions for tooltip
  const activePermissions = Object.entries(
    permissions as Record<string, boolean>
  )
    .filter(([, value]) => value)
    .map(([key]) => key);

  const badge = (
    <Badge
      variant={variant}
      className={cn('flex items-center gap-1.5 px-2.5 py-1', className)}
    >
      <Icon className="size-3.5" aria-hidden="true" />
      <span className="text-xs font-medium">{roleLabel}</span>
    </Badge>
  );

  if (compact) {
    return badge;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-xs">
          <div className="space-y-2 text-xs">
            <div className="font-semibold">
              {user?.name ?? 'User'} ({roleEnglish})
            </div>
            <div className="text-muted-foreground">
              {activePermissions.length} active permissions:
            </div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-muted-foreground">
              {activePermissions.slice(0, 10).map((perm) => (
                <li key={perm}>
                  <TruncatedText>{`• ${perm.replace('can', '')}`}</TruncatedText>
                </li>
              ))}
              {activePermissions.length > 10 && (
                <li className="col-span-2 text-muted-foreground/70">
                  +{activePermissions.length - 10} more...
                </li>
              )}
            </ul>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
